import { z } from 'zod';
import { ASSIGNABLE_ROLES, MEMBERSHIP_STATUSES } from './team.js';

/** الحد الأدنى لكلمة المرور — نفسه في التسجيل والدعوة وتغيير الكلمة. */
export const PASSWORD_MIN = 10;

export const PasswordSchema = z
  .string()
  .min(PASSWORD_MIN, `كلمة المرور ${PASSWORD_MIN} أحرف على الأقل.`)
  .max(200, 'كلمة المرور أطول من اللازم.');

const AssignableRole = z.enum(ASSIGNABLE_ROLES as [string, ...string[]], {
  errorMap: () => ({ message: 'دور غير صالح.' }),
});

export const InviteMemberSchema = z.object({
  email: z.string().trim().toLowerCase().email('صيغة البريد الإلكتروني غير صحيحة.'),
  name: z.string().trim().min(2, 'الاسم مطلوب.').max(120),
  role: AssignableRole,
  /**
   * إنشاء ملف موظف مع الدعوة.
   *
   * أغلب من يُدعى إلى النظام موظف في الشركة فعلًا، وفصل الخطوتين يترك
   * قائمة مستخدمين وقائمة موظفين لا تتطابقان.
   */
  createEmployee: z.boolean().default(false),
  jobTitle: z.string().trim().max(120).optional(),
  /**
   * ربطُ الحسابِ بملفِّ موظفٍ قائمٍ بلا حساب.
   *
   * حين يُنشأ الحسابُ لموظفٍ له ملفٌّ في الموارد البشرية، يُربط الملفُّ
   * بالعضويةِ الجديدةِ بدل إنشاء ملفٍّ ثانٍ — فلا يزدوج الموظّفُ ملفين.
   */
  employeeId: z.string().regex(/^[a-z0-9]{20,32}$/i).nullable().optional(),
  /**
   * كيف يدخل المدعوّ أوّلَ مرّة.
   *
   * رابطٌ يضبط به كلمتَه بنفسه (الأصل)، أو كلمةُ مرورٍ مؤقّتة تُرسل إلى
   * بريده ويُطلب منه تغييرُها عند أوّل دخول — لمن يريد تسليمَ الحساب
   * جاهزًا بلا انتظار.
   */
  delivery: z.enum(['LINK', 'TEMP_PASSWORD']).default('LINK'),
  /** ملفُّ الصلاحيات — اختياريّ، والدورُ وحدَه يكفي. */
  profileId: z.string().regex(/^[a-z0-9]{20,32}$/i).nullable().optional(),
});

export type InviteMemberInput = z.infer<typeof InviteMemberSchema>;

export const UpdateMemberSchema = z.object({
  role: AssignableRole.optional(),
  profileId: z.string().regex(/^[a-z0-9]{20,32}$/i).nullable().optional(),
  status: z.enum(['ACTIVE', 'SUSPENDED'], { errorMap: () => ({ message: 'حالة غير صالحة.' }) })
    .optional(),
});

export type UpdateMemberInput = z.infer<typeof UpdateMemberSchema>;

export const MemberListQuerySchema = z.object({
  q: z.string().trim().max(120).optional(),
  role: z.enum(['OWNER', 'ADMIN', 'MANAGER', 'SALES', 'EMPLOYEE']).optional(),
  status: z.enum(MEMBERSHIP_STATUSES).optional(),
});

export type MemberListQuery = z.infer<typeof MemberListQuerySchema>;

/** قبول الدعوة — مسار عام، فالمدعو لا يملك حسابًا بعد. */
export const AcceptInvitationSchema = z.object({
  token: z.string().trim().min(20, 'رابط الدعوة غير صالح.').max(200),
  name: z.string().trim().min(2, 'الاسم مطلوب.').max(120).optional(),
  password: PasswordSchema,
});

export type AcceptInvitationInput = z.infer<typeof AcceptInvitationSchema>;

/**
 * نقل الملكية.
 *
 * الشركة لا تبقى بلا مالك ولا يكون لها مالكان: العملية تبادل في معاملة
 * واحدة — المالك الحالي يصير مديرًا، والمستهدف يصير مالكًا.
 */
export const TransferOwnershipSchema = z.object({
  membershipId: z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.'),
  password: z.string().min(1, 'كلمة مرورك مطلوبة للتأكيد.'),
});

export type TransferOwnershipInput = z.infer<typeof TransferOwnershipSchema>;
